import { useEffect, useRef, useState } from "react";
import { dropDownWhite } from "../../asset";
import CustomButton from "../../components/CustomButton";
import DataTable, { Column } from "../../components/DataTable";
import ClassHeaderSection from "../classes-details/ClassHeaderSection";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import {
  clearStudents,
  getStudents,
  setPagination,
  setSearchFilters,
} from "../../features/student/StudentSlice";
import { useParams, useSearchParams } from "react-router-dom";
import { Student } from "../../types/studentType";
import attendanceApis from "../../api/attendanceApis";
import { isOlderThan15Days } from "../../util/functions/UniversalFunctions";
import { toast } from "sonner";

export default function AttendanceDetails() {
  const [attendance, setAttendance] = useState<
    Record<number, "PRESENT" | "ABSENT" | "HALF_DAY">
  >({});
  const [searchParams] = useSearchParams();
  const queryDate = searchParams.get("date");

  const [selectedDate, setSelectedDate] = useState(
    queryDate || new Date().toISOString().split("T")[0],
  );

  const { classId } = useParams();
  const dateInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { students, isLoading, pagination, searchFilters } = useSelector(
    (state: RootState) => state.student,
  );

  const { schoolBranch } = useSelector((state: RootState) => state.access);

  const branchCode = schoolBranch?.schoolBranchCode;

  useEffect(() => {
    if (!classId) return;

    dispatch(
      getStudents({
        branchCode,
        classId: Number(classId),
        page: 0,
        size: 1000,
        studentName: searchFilters.studentName || "",
      }),
    );
    return () => {
      dispatch(clearStudents());
    };
  }, [dispatch, classId, branchCode, searchFilters.studentName]);

  const handleMarkAttendance = async (
    admissionNumber: number,
    status: "PRESENT" | "ABSENT",
  ) => {
    if (!branchCode || !classId) return;

    if (isOlderThan15Days(selectedDate)) {
      toast.error("Attendance cannot be marked for dates older than 15 days.");
      return;
    }

    const existing = attendance[admissionNumber];

    if (existing === status) return; // prevent duplicate API call

    const payload = {
      admissionNumber,
      attendanceDate: selectedDate,
      status,
      classId: Number(classId),
    };

    const res = await attendanceApis.markStudentAttendance(payload, branchCode);

    if (res?.status === 1) {
      setAttendance((prev) => ({
        ...prev,
        [admissionNumber]: status,
      }));
    }
  };
  // sync date from URL
  useEffect(() => {
    if (queryDate) {
      setSelectedDate(queryDate);
    }
  }, [queryDate]);

  // fetch attendance
  useEffect(() => {
    if (!classId) return;

    const fetchAttendance = async () => {
      const res = await attendanceApis.getStudentDayWiseAttendance(
        Number(classId),
        selectedDate,
      );

      if (res?.status === 1) {
        const mapped: Record<number, "PRESENT" | "ABSENT" | "HALF_DAY"> = {};
        res.data?.forEach((item) => {
          mapped[item.admissionNumber] = item.status;
        });
        setAttendance(mapped);
      } else {
        setAttendance({});
      }
    };

    fetchAttendance();
  }, [classId, selectedDate]);

  // console.log("date from url:", queryDate, "state:", selectedDate);

  const columns: Column<Student>[] = [
    {
      label: "SL NO.",
      accessor: "admissionNumber",
      searchable: false,
    },
    {
      label: "Profile",
      accessor: "profile",
      searchable: false,
      render: (item) => (
        <div>
          {!item?.photoUrl ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.photoUrl}
              alt=""
              className="flex items-center justify-center object-cover w-10 h-10 rounded-full"
            />
          )}
        </div>
      ),
    },
    {
      label: "Student Name",
      accessor: "studentName",
      searchable: true,
      render: (item) => (
        <div>
          <div className="font-bold text-sm text-neutral-400">
            {item.fullName}
          </div>
        </div>
      ),
    },
    {
      label: "Present",
      accessor: "present",
      render: (item) => (
        <input
          type="radio"
          name={`attendance-${item.admissionNumber}`}
          checked={attendance[item.admissionNumber] === "PRESENT"}
          onChange={() =>
            handleMarkAttendance(Number(item.admissionNumber), "PRESENT")
          }
          className="w-5 h-5 appearance-none border-2 border-neutral-400 rounded-full checked:bg-primary-500 checked:border-primary-500 cursor-pointer"
        />
      ),
    },

    {
      label: "Absent",
      accessor: "absent",
      render: (item) => (
        <input
          type="radio"
          name={`attendance-${item.admissionNumber}`}
          checked={attendance[item.admissionNumber] === "ABSENT"}
          onChange={() =>
            handleMarkAttendance(Number(item.admissionNumber), "ABSENT")
          }
          className="w-5 h-5 appearance-none border-2 border-neutral-400 rounded-full checked:bg-error-500 checked:border-error-500 cursor-pointer"
        />
      ),
    },
  ];

  return (
    <div className="grid gap-3">
      <ClassHeaderSection totalStudents={students.length} />
      <div className="grid gap-1">
        <div className="bg-white rounded-lg py-2 items-center px-4 flex gap-7 w-fit">
          <label className="text-neutral-400 text-sm font-semibold">Date</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => dateInputRef.current?.showPicker()}
              className="bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-3"
            >
              {selectedDate.split("-").reverse().join("/")}
              <img src={dropDownWhite} alt="dropdown" />
            </button>

            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="absolute opacity-0 pointer-events-none w-0 h-0"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg py-2 items-center px-4 flex gap-7 overflow-x-auto">
          <DataTable
            columns={columns}
            data={students}
            isLoading={isLoading}
            searchFilters={searchFilters}
            onSearchChange={(accessor, value) => {
              dispatch(setSearchFilters({ [accessor]: value }));
              dispatch(
                setPagination({
                  currentPage: 0,
                  pageSize: pagination.pageSize,
                }),
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
